import classnames from 'classnames';
import { isEmpty, keys, map } from 'lodash';
import { flow as flowFP, groupBy as groupByFP } from 'lodash/fp';
import React from 'react';
import AssetIcon from '../../components/icon/common/AssetIcon';
import Activity from '../../components/timeline/Activity';
import { DETAILED_STATUS } from '../../constants/job';
import {
  ASSET_ICON_MAP,
  OTHER_PRODUCTS,
  STATUS_COLOR_MAP,
  STATUS_TEXT_MAP,
} from '../../constants/product';
import text from '../../text';
import { formatDate, formatTime } from '../../utilities/date';
import { getProductLabel } from '../../utilities/activity';
import { IconWrapper } from '../../components/RightMenu/Product';
import Icon from '@/components/icon/Icon';
import AssetIconV2 from '../../components/icon/common/AssetIconV2';
import { isDerivedDataType } from '@larki/mp/lib/dataType';

const JobStatusUpdate = (props) => {
  const {
    activityTitle,
    activityDescription,
    detailedStatuses,
    createdDate,
    jobId,
    projectId,
    isHidden,
    menu,
  } = props;

  // We asume that a status activity with not detailed status is by default a 'draft'
  const groupedUpdates = !isEmpty(detailedStatuses)
    ? flowFP([
        (result) => map(result, (value, product) => ({ ...value, product })),
        groupByFP('status'),
      ])(detailedStatuses)
    : ['Draft'];

  // One activity renders a row per distinct status; the menu acts on the whole
  // activity, so it belongs on the first row only.
  const [firstGroupKey] = keys(groupedUpdates);

  return map(groupedUpdates, (products, prodStatus) => {
    return (
      <Activity key={prodStatus} className='JobStatusUpdate'>
        <>
          <Activity.Icon
            iconProps={{
              icon: 'circle',
              className: !isEmpty(detailedStatuses)
                ? STATUS_COLOR_MAP[prodStatus]
                : STATUS_COLOR_MAP.Draft,
            }}
          />
          <div className={classnames('DetailedStatus w-100', { isHidden })}>
            <Activity.Card.Header className='d-flex flex-column pb-1'>
              {/* w-100 replaces an inert justify-content-center: the header is a
                  column flex with baseline alignment, so the row was
                  content-width and HeaderRight's margin-left:auto had no room. */}
              <div className='font-weight--500 font-12 d-flex flex-row w-100'>
                <Activity.Card.Type type={activityTitle} />
                <Activity.Card.Date
                  date={formatDate(createdDate)}
                  time={formatTime(createdDate, 'h:mmaaa')}
                />
                {projectId ? (
                  <Activity.Card.HeaderElement
                    className={classnames(
                      {
                        hasPipeSeparator: !!jobId,
                      },
                      'projectId py-0'
                    )}
                  >
                    {text('projectId')}:{projectId}
                  </Activity.Card.HeaderElement>
                ) : null}

                {jobId ? (
                  <Activity.Card.HeaderElement className='jobId py-0'>
                    {text('orderId')}:{jobId}
                  </Activity.Card.HeaderElement>
                ) : null}

                {String(prodStatus) === firstGroupKey ? (
                  <Activity.Card.HeaderRight>{menu}</Activity.Card.HeaderRight>
                ) : null}
              </div>
            </Activity.Card.Header>
            <div className='DetailedStatusContent'>
              {!isEmpty(detailedStatuses)
                ? map(products, ({ product, status, expectedAt }, i) => {
                    const matchedOther =
                      OTHER_PRODUCTS.find(
                        ({ label }) =>
                          typeof product === 'string' && product.includes(label)
                      ) || null;
                    const assetIcon =
                      ASSET_ICON_MAP[product] ??
                      (matchedOther ? matchedOther.icon : null);
                    const isDerived = isDerivedDataType(product.toLowerCase());
                    return (
                      <div key={i} className='DetailedStatusContent-Item'>
                        <AssetIconV2
                          {...(assetIcon && !matchedOther
                            ? {
                                icon:
                                  isDerived ||
                                  product.toLowerCase() === 'unknown'
                                    ? `${product.toLowerCase()}_white`
                                    : product.toLowerCase(),
                              }
                            : { icon: assetIcon })}
                          classNames={classnames('mr-2', assetIcon, {
                            'inverted rounded-corners': !matchedOther,
                          })}
                          defaultIcon='cube'
                        />
                        <span>
                          {assetIcon && !matchedOther
                            ? isDerived
                              ? `${getProductLabel(product)} order: `
                              : text('productPointCloud3DOrder', {
                                  productType: getProductLabel(product),
                                })
                            : `${product}: `}
                          <strong>{` ${STATUS_TEXT_MAP[status]}`}</strong>
                          <span>
                            {' '}
                            {expectedAt && status !== DETAILED_STATUS.COMPLETED
                              ? text('expectedAt', {
                                  eta: formatDate(expectedAt),
                                })
                              : null}
                          </span>
                        </span>
                      </div>
                    );
                  })
                : activityDescription}
            </div>
          </div>
        </>
      </Activity>
    );
  });
};

export default JobStatusUpdate;
